/** biome-ignore-all lint/style/useNamingConvention: allow camel case */
import cx from 'classnames';
import {ReactNode, SyntheticEvent, useCallback, useRef, useState} from 'react';
import {Popover} from 'react-tiny-popover';
import {isFabricWebview} from '~/common/utils/webview';
import {Button} from '~library/atoms/Button';
import {Link} from '~library/atoms/Link';
import {AppleIcon} from '~library/svg/AppleIcon';
import {GoogleIcon} from '~library/svg/GoogleIcon';
import {OutlookIcon} from '~library/svg/OutlookIcon';
import {CalendarAddIcon} from '~library/svg/v2/CalendarAddIcon';
import {CalendarIcon} from '~library/svg/v2/CalendarIcon';

import './AddToCalendar.sass';

const CalendarButton = ({
  url,
  qaId,
  text,
  icon,
}: {
  url?: string;
  qaId: string;
  text: string;
  icon: ReactNode;
}) =>
  url && (
    <Link
      as="transparent"
      variant="small"
      className="add-to-calendar__option"
      href={url}
      target="_blank"
      data-qa-id={qaId}
    >
      {icon}
      {text}
    </Link>
  );

interface AddToCalendarProps {
  isPreview?: boolean;
  'data-type'?: string;
  'data-qa-id'?: string;
  'data-source'?: string;
  'data-action'?: string;
}

export const AddToCalendar = ({
  isPreview = false,
  'data-type': dataType = 'calendar',
  'data-qa-id': dataQaId,
  'data-source': dataSource = '',
  'data-action': dataAction = 'download',
}: AddToCalendarProps) => {
  const ref = useRef(null);
  const inWebview = window.is_webview || isFabricWebview();
  const [menuOpen, setMenuOpen] = useState(false);
  const googleCalendarUrl = window.calendar_urls?.google_cal_url;
  const iCalUrl = window.calendar_urls?.ical_url;
  const outlookUrl = window.calendar_urls?.outlook_cal_url;

  const handleClick = useCallback((event: MouseEvent | SyntheticEvent<HTMLButtonElement>) => {
    event.preventDefault();
    setMenuOpen((prevState) => !prevState);
  }, []);

  const calendarLinksAvailable = googleCalendarUrl || iCalUrl || outlookUrl;

  if (inWebview) return null;

  return (
    calendarLinksAvailable && (
      <Popover
        isOpen={menuOpen}
        padding={8}
        onClickOutside={handleClick}
        positions={['right', 'left', 'top']}
        content={
          <div id="calendar-options-menu" className="add-to-calendar__calendar-menu">
            <CalendarButton
              url={googleCalendarUrl}
              qaId="add-to-cal__google"
              text="Add to Google calendar"
              icon={<GoogleIcon ratio={0.75} />}
            />
            <CalendarButton
              url={iCalUrl}
              qaId="add-to-cal__apple"
              text="Add to Apple calendar"
              icon={<AppleIcon ratio={0.75} />}
            />
            <CalendarButton
              url={outlookUrl}
              qaId="add-to-cal__outlook"
              text="Add to Outlook calendar"
              icon={<OutlookIcon ratio={0.75} />}
            />
            <CalendarButton
              url={iCalUrl}
              qaId="add-to-cal__apple"
              text="Download iCal"
              icon={<CalendarIcon ratio={0.75} />}
            />
          </div>
        }
      >
        <Button
          id="add-to-calendar"
          variant="transparent"
          data-qa-id={dataQaId ?? 'add-to-calendar'}
          className={cx('add-to-calendar-btn', {
            disabled: isPreview,
          })}
          title="Add to calendar"
          data-type={dataType}
          data-source={dataSource}
          data-action={dataAction}
          onClick={handleClick}
          disabled={isPreview}
          ref={ref}
          type="button"
        >
          <CalendarAddIcon ratio={0.75} />
        </Button>
      </Popover>
    )
  );
};
